// API helper for the E-Learning admin endpoints (Phase 18.1).
// All routes are prefixed with /api and require an authenticated ops user.

function backendBase() {
  // Stay consistent with the rest of the codebase (process.env reads work in CRA / Vite shim).
  return process.env.REACT_APP_BACKEND_URL || '';
}

function authHeader() {
  const token = localStorage.getItem('healthcues_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function jsonOrThrow(response) {
  let body = null;
  try { body = await response.json(); } catch (_) { /* no-op */ }
  if (!response.ok) {
    const detail = body && body.detail;
    if (detail && typeof detail === 'object') {
      // FastAPI structured error (e.g. publish validation)
      const err = new Error(detail.message || 'Request failed');
      err.errors = detail.errors || [];
      err.status = response.status;
      throw err;
    }
    const err = new Error((body && body.detail) || `Request failed (${response.status})`);
    err.status = response.status;
    throw err;
  }
  return body;
}

export async function listCourses({ audience, status } = {}) {
  const params = new URLSearchParams();
  if (audience) params.set('audience', audience);
  if (status) params.set('status', status);
  const qs = params.toString();
  const url = `${backendBase()}/api/learning/courses${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: { ...authHeader() } });
  return jsonOrThrow(res);
}

export async function getCourse(id) {
  const res = await fetch(`${backendBase()}/api/learning/courses/${encodeURIComponent(id)}`, {
    headers: { ...authHeader() },
  });
  return jsonOrThrow(res);
}

export async function createCourse(payload) {
  const res = await fetch(`${backendBase()}/api/learning/courses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res);
}

export async function updateCourse(id, payload) {
  const res = await fetch(`${backendBase()}/api/learning/courses/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res);
}

export async function publishCourse(id) {
  const res = await fetch(`${backendBase()}/api/learning/courses/${encodeURIComponent(id)}/publish`, {
    method: 'POST',
    headers: { ...authHeader() },
  });
  return jsonOrThrow(res);
}

export async function archiveCourse(id) {
  return updateCourse(id, { status: 'archived' });
}

export async function unpublishCourse(id) {
  // Move back to draft (e.g. to allow editing).
  return updateCourse(id, { status: 'draft' });
}

export async function deleteCourse(id) {
  const res = await fetch(`${backendBase()}/api/learning/courses/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { ...authHeader() },
  });
  return jsonOrThrow(res);
}

// ----------------------------------------------------------------------------
// Phase 18.3 — Certificates (admin scope)
// ----------------------------------------------------------------------------

export async function listAllCertificates(filters = {}) {
  const params = new URLSearchParams();
  if (filters.audience) params.set('audience', filters.audience);
  if (filters.courseId) params.set('courseId', filters.courseId);
  if (filters.status) params.set('status', filters.status);
  if (filters.q) params.set('q', filters.q);
  if (filters.dateFrom) params.set('date_from', filters.dateFrom);
  if (filters.dateTo) params.set('date_to', filters.dateTo);
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();
  const url = `${backendBase()}/api/learning/certificates${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: { ...authHeader() } });
  return jsonOrThrow(res);
}

export async function revokeCertificate(certId) {
  const res = await fetch(`${backendBase()}/api/learning/certificates/${encodeURIComponent(certId)}/revoke`, {
    method: 'POST',
    headers: { ...authHeader() },
  });
  return jsonOrThrow(res);
}

export async function reinstateCertificate(certId) {
  const res = await fetch(`${backendBase()}/api/learning/certificates/${encodeURIComponent(certId)}/reinstate`, {
    method: 'POST',
    headers: { ...authHeader() },
  });
  return jsonOrThrow(res);
}

export function certificateVerifyUrl(certId) {
  // Public verification page (rendered by the learner verify route).
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return `${window.location.origin}/verify-certificate/${encodeURIComponent(certId)}`;
  }
  return `/verify-certificate/${encodeURIComponent(certId)}`;
}

export const AUDIENCE_OPTIONS = [
  { value: 'account_manager', label: 'Account Managers' },
  { value: 'employer', label: 'Employers' },
  { value: 'employee', label: 'Employees' },
];

export const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

export function audienceLabel(value) {
  const m = AUDIENCE_OPTIONS.find((o) => o.value === value);
  return m ? m.label : value;
}

export function statusLabel(value) {
  const m = STATUS_OPTIONS.find((o) => o.value === value);
  return m ? m.label : value;
}
